import { useEffect, useState } from 'react';

import { iaRa } from 'lib/helpers/utility';

import PatientChoose from 'components/Patient/checkin/SecurePayment/components/PreAuthModal/PatientChoose';
import RecurringDetails from 'components/Payments/NewPayment/PaymentContent/components/RecurringPayments/components/RecurringDetails/RecurringDetails';
import RecurringInfo from 'components/Payments/NewPayment/PaymentContent/components/RecurringPayments/components/RecurringInfo/RecurringInfo';
import { usePaymentContext } from 'components/Payments/NewPayment/PaymentContent/context/PaymentContext';
import Divider from 'components/shared/Divider/Divider';
import Icon from 'components/shared/Icon/Icon';
import Typography from 'components/shared/Typography/Typography';

import { usePreAuthForm } from '../../PreAuthModal/lib/usePreAuthForm';
import { useLetter } from '../LetterContext';
import { allowedCards, getDefaultScope, getRecurringCaps } from '../lib/helpers';

import InteractivePreAuthBody from './InteractivePreAuthBody';
import PreAuthInputsPreview from './PreAuthInputsPreview';
import PreAuthStatement from './PreAuthStatement';
import PreAuthTerms from './PreAuthTerms';

const InteractivePreAuth = ({ scope }) => {
  const {
    appointment,
    patientName,
    practiceId,
    patientId,
    secureTokenId,
    onCancelPreAuth,
    timezone
  } = useLetter();
  const { formik, stampDate } = usePreAuthForm({
    appointment,
    patientName,
    practiceId,
    patientId,
    timezone,
    defaultScope: scope
  });

  const { setFieldValue } = formik;
  useEffect(() => {
    if (scope) setFieldValue('scope', scope);
  }, [scope, setFieldValue]);

  return (
    <InteractivePreAuthBody
      formik={formik}
      appointment={appointment}
      secureTokenId={secureTokenId}
      onCancel={onCancelPreAuth}
      stampDate={stampDate}
    />
  );
};

const PreAuthFormSection = () => {
  const { mode, config, activePhaseField, amounts, appointment } = useLetter();
  const phase = iaRa(config?.phases).find((p) => p.field === activePhaseField);
  const oonResponsibility = appointment?.oon_patient_responsibility ?? null;
  const isPatientChoose = phase?.authorization_applied?.route_to === 'let-patient-choose';
  const defaultScope = getDefaultScope(phase);
  const [selectedScope, setSelectedScope] = useState(defaultScope);
  const paymentCtx = usePaymentContext();
  const isRecurringPlan = paymentCtx?.values?.method === 'recurring-charge';
  const recurringCaps = getRecurringCaps(config);

  return (
    <div className="flex w-full flex-col gap-6 bg-neutral-50 p-5">
      <div className="flex flex-col gap-2">
        <Typography variant="p" size="small" color="neutral-600" className="italic">
          Regulated under card-brand standards for healthcare transactions.
        </Typography>
        <div className="flex items-center gap-1">
          {allowedCards.map((item) => (
            <Icon key={item.id} icon={item.icon} size={22} />
          ))}
        </div>
      </div>
      {isPatientChoose && <PatientChoose value={selectedScope} onChange={setSelectedScope} />}
      <PreAuthStatement
        phase={phase}
        selectedScope={isPatientChoose ? selectedScope : null}
        amounts={amounts}
        oonResponsibility={oonResponsibility}
      />
      <PreAuthTerms phase={phase} amounts={amounts} oonResponsibility={oonResponsibility} />
      <Divider />
      {mode === 'interactive' ? (
        <InteractivePreAuth scope={isPatientChoose ? selectedScope : defaultScope} />
      ) : (
        <PreAuthInputsPreview />
      )}
      {isRecurringPlan && (
        <>
          <Divider />
          <div className="flex flex-col gap-3">
            <Typography variant="p" size="medium" weight="medium">
              Payment plan
            </Typography>
            <RecurringDetails caps={recurringCaps} />
            <RecurringInfo subject="You" />
          </div>
        </>
      )}
    </div>
  );
};

export default PreAuthFormSection;
